import { useState, useEffect} from 'react';
import { useTranslation } from 'react-i18next';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { DetailSection } from '../components/ui/DetailSection.js';
import { Pill } from '../components/PillGroup.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { useConfirm } from '../components/ui/ConfirmDialog.js';
import { useToast } from '../components/ui/Toast.js';
import { parseServerDate } from '../lib/date.js';
import { ApiError } from '../lib/api.js';
import {
  usePendingSignupRequests,
  useApproveSignupRequest,
  useRejectSignupRequest,
  type SignupRequestRow,
} from '../hooks/usePlatformAdmin.js';

function fmtDate(s: string): string {
  return parseServerDate(s).toLocaleString('es', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Registro público (2026-08-05) — solo is_platform_admin llega hasta acá con
// éxito (403 real en el backend, ver requirePlatformAdmin); esta pantalla
// solo reusa el patrón visual de fila de UsersSection en Settings — sin
// diseño nuevo, sin DataTable, sin sidebar nav nueva (ruta directa).
function RequestRow({ request }: { request: SignupRequestRow }) {
  const { t } = useTranslation();
  const [slug, setSlug] = useState(request.suggestedSlug);
  const approve = useApproveSignupRequest();
  const reject = useRejectSignupRequest();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();
  const toast = useToast();

  async function handleApprove() {
    try {
      await approve.mutateAsync({ id: request.id, slug });
      toast.success(t('platformAdmin.approved', { business: request.businessName }));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function handleReject() {
    const ok = await confirmAction({ title: t('platformAdmin.rejectConfirm', { business: request.businessName }), tone: 'danger' });
    if (!ok) return;
    try {
      await reject.mutateAsync(request.id);
      toast.success(t('platformAdmin.rejected', { business: request.businessName }));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    }
  }

  return (
    <div
      className="flex items-center justify-between"
      style={{ gap: 'var(--evo-space-3)', padding: 'var(--evo-space-3) 0', borderBottom: '1px solid var(--evo-stroke)', minHeight: 'var(--evo-action-row-min-h)', boxSizing: 'border-box' }}
    >
      {confirmDialog}
      <div className="flex flex-col" style={{ minWidth: 0 }}>
        <span style={{ fontSize: 'var(--evo-list-title-fs)', fontWeight: 500, color: 'var(--evo-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{request.personName} — {request.businessName}</span>
        <span style={{ marginTop: 2, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{request.email} · {fmtDate(request.createdAt)}</span>
      </div>
      <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
        <Input value={slug} onChange={setSlug} size="sm" />
        <Button variant="solid" tone="cyan" size="sm" loading={approve.isPending} onClick={() => { void handleApprove(); }}>
          {t('platformAdmin.approveBtn')}
        </Button>
        <Button variant="outline" tone="muted" size="sm" loading={reject.isPending} onClick={() => { void handleReject(); }}>
          {t('platformAdmin.rejectBtn')}
        </Button>
      </div>
    </div>
  );
}

export function PlatformAdminPage() {
  const { t } = useTranslation();
  const { data, isLoading } = usePendingSignupRequests();
  const { setOverride } = useTopBarCtx();

  // El título va en la banda superior, igual que en el resto de la app — no
  // adentro del contenido con la banda vacía arriba.
  useEffect(() => {
    setOverride({
      title: t('platformAdmin.title'),
      subtitle: t('platformAdmin.subtitle'),
      heroIcon: 'admin',
      heroIconColor: 'var(--evo-cyan)',
    });
    return () => setOverride({});
  }, [t, setOverride]);

  const requests = data?.requests ?? [];

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: 'var(--evo-space-6) var(--evo-page-pad-x)' }}>
      <div style={{ maxWidth: '900px' }}>
        <DetailSection
          title={t('platformAdmin.pendingSection', { defaultValue: 'Pendientes de aprobación' })}
          titleRight={requests.length > 0 ? <Pill size="xs" variant="outline" tone="cyan">{requests.length}</Pill> : undefined}
        >
          {isLoading && <LoadingState />}
          {!isLoading && requests.length === 0 && (
            <EmptyState title={t('platformAdmin.empty')} />
          )}
          {!isLoading && requests.length > 0 && (
            <div className="flex flex-col">
              {requests.map(r => <RequestRow key={r.id} request={r} />)}
            </div>
          )}
        </DetailSection>
      </div>
    </div>
  );
}
